// Forgot Password Page Component
// ================================

import React, { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

import Button from '../ui/Button';
import Input from '../ui/Input';
import { LyraLogo, ContentFrame } from '../ui';

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { sendPasswordReset } = useAuth();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      const result = await sendPasswordReset(email);
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || t('forgotPassword.defaultError'));
      }
    } catch (err) {
      setError(tErrors('auth.passwordResetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark px-4">
      <div className="w-full max-w-md">
        {/* Logo and title - Feature #155: SVG logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <LyraLogo size="lg" showText={true} />
          </div>
          <p className="text-neutral-medium">{tCommon('tagline')}</p>
        </div>

        {/* Forgot password form card - Feature #288: Updated with ContentFrame and new design */}
        <ContentFrame
          variant="border"
          padding="lg"
          showAccent={true}
        >
          {/* Feature #280: Serif display font for headings */}
          <h2 className="text-2xl font-display font-semibold text-neutral-dark dark:text-white mb-2 text-center">
            {t('forgotPassword.title')}
          </h2>
          <p className="text-neutral-medium text-sm font-sans text-center mb-6">
            {t('forgotPassword.description')}
          </p>

          {error && (
            <div
              className="mb-4 p-3 bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800 rounded-lg"
              role="alert"
              aria-live="assertive"
            >
              <p className="text-accent-600 dark:text-accent-400 text-sm text-center font-sans">{error}</p>
            </div>
          )}

          {success ? (
            <div
              className="mb-4 p-4 bg-secondary-50 dark:bg-secondary-900/20 border border-secondary-200 dark:border-secondary-800 rounded-lg"
              role="status"
              aria-live="polite"
            >
              <div className="text-center">
                <div className="text-2xl mb-2">✉️</div>
                <p className="text-secondary-700 dark:text-secondary-400 font-medium font-sans mb-1">
                  {t('forgotPassword.successTitle')}
                </p>
                <p className="text-secondary-600 dark:text-secondary-400 text-sm font-sans">
                  {t('forgotPassword.successMessage')}
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-sans font-medium text-neutral-dark dark:text-white mb-1">
                  {t('forgotPassword.email')}
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('forgotPassword.emailPlaceholder')}
                  required
                  autoComplete="email"
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={isLoading}
              >
                {isLoading ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-primary-600 hover:text-primary-700 font-sans font-medium transition-colors text-sm"
            >
              {t('forgotPassword.backToLogin')}
            </Link>
          </div>
        </ContentFrame>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
